/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Table, Spin, Button, Modal, DatePicker, Input } from 'antd'
import '@styles'
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { ColumnsType } from 'antd/es/table'

import { useGetBatchees, useInputHes, useUserResponseManager } from '@hooks'
import { useFilters, useGeneral, useUser } from '@contexts'
import { DataApi } from '@api'
import { Batch } from '@types'

import {
  ActionsCellContainer,
  ContentContainer,
  StyledInput,
  StyledRow,
  StyledSelect,
} from '../StyledComponents'
import sortBySetter from '../../utils/sort-by-setter'

interface BatchItem {
  carrier: { id: number; businessName: string }
  closeDate: string | null
  createAt: string
  hes: string
  hesDate: string
  id: number
  openDate: string
  status: string
  tickets: []
  updateAt: string
}

const ClosedAdmin = () => {
  const { t } = useTranslation()
  const { filters, setFilters } = useFilters()
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { billingBatch, isLoading } = useGetBatchees()
  const openStatusObjects = billingBatch.filter((item: Batch) => item.status === 'CLOSED')
  const [filteredData, setFilteredData] = useState<BatchItem[]>(openStatusObjects as BatchItem[])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([])
  const [uniqueCarrierNames, setUniqueCarrierNames] = useState<string[]>([])
  const [loteFilter, setLoteFilter] = useState<string>()
  const [dateFilter, setDateFilter] = useState<Dayjs | null>(null)
  const [inputIdHes, setinputIdHes] = useState<number>(0)
  const [numero, setNumero] = useState('')
  const { inputHes } = useInputHes()
  const { state: user } = useUser()
  const { getUser } = useUserResponseManager(user?.id)

  const handleCarrierNameSearch = (selectedValues: any) => {
    setSelectedCarriers(selectedValues as string[])
    filterByCarrierNames(selectedValues as string[])
  }

  const filterByCarrierNames = (selectedValues: string[]) => {
    const data = openStatusObjects.filter(
      (item: Batch) =>
        selectedValues.length === 0 ||
        selectedValues.some(
          value => item.carrier.businessName.toLowerCase() === value.toLowerCase(),
        ),
    ) as BatchItem[]
    setFilteredData(data)
  }

  const handleDownloadExcel = (id: number, businessName: string) => {
    DataApi.getBillingBatchExcel(id).then(res => {
      const link = document.createElement('a')
      const url = window.URL.createObjectURL(res)
      link.href = url
      document.body.appendChild(link)
      link.setAttribute('download', `Lote-${id}-${businessName}.xlsx`)
      link.click()
      link.parentNode?.removeChild(link)
    })
  }

  useEffect(() => {
    if (!isLoading) {
      const uniqueNamesSet = new Set(billingBatch.map(batch => batch.carrier.businessName))
      const uniqueNamesArray = Array.from(uniqueNamesSet)
      setUniqueCarrierNames(uniqueNamesArray)
      const fetchData = async () => {
        setFilteredData(
          billingBatch.filter((item: Batch) => item.status === 'CLOSED') as BatchItem[],
        )
      }

      fetchData()
    }
  }, [billingBatch, isLoading])

  const handleHesFilterChangeLote = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setLoteFilter(value)
    const data = openStatusObjects.filter(
      item => value.trim() === '' || item.id.toString().includes(value.trim()),
    )

    setFilteredData(data as BatchItem[])
  }

  const handleDateFilterChange = (date: Dayjs, dateString: string | string[]) => {
    setDateFilter(date)
    filterByDate(
      typeof dateString === 'string' ? (dateString as string) : (dateString[0] as string),
    )
  }

  const handleInputHes = (id: number) => {
    setinputIdHes(id)
    setIsModalVisible(true)
  }

  const filterByDate = (dateString: string) => {
    const data = openStatusObjects.filter(
      item =>
        dateString.trim() === '' ||
        (item.createAt && dayjs(item.createAt).isSame(dateString, 'day')),
    ) as BatchItem[]
    setFilteredData(data)
  }

  const handleNumeroChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNumero(e.target.value)
  }
  const handleOk = () => {
    inputHes(numero, inputIdHes)
    setIsModalVisible(false)
    setNumero('')
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setNumero('')
  }

  const columnsBatch: ColumnsType = [
    {
      title: 'ID Lote',
      dataIndex: 'id',
      key: 'id',
      sorter: sortBySetter('id'),
      sortDirections: ['ascend'],
    },
    {
      title: 'Transportista',
      dataIndex: ['carrier', 'businessName'],
      key: 'carrierName',
    },
    {
      title: 'Lote #',
      dataIndex: 'carrierSeq',
      key: 'carrierSeq',
    },
    {
      title: 'Desde',
      dataIndex: 'createAt',
      key: 'createAt',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => (dayjs(b.createAt).isAfter(dayjs(a.createAt)) ? 1 : -1),
    },
    {
      title: 'Hasta',
      dataIndex: 'closeDate',
      key: 'closeDate',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => (dayjs(b.closeDate).isAfter(dayjs(a.closeDate)) ? 1 : -1),
    },
    {
      title: 'Viajes',
      dataIndex: 'tickets',
      key: 'tickets',
      render: (tickets: string | unknown[]) => <span>{tickets?.length}</span>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      render: (record: { id: number; carrier: { id: number; businessName: string } }) =>
        getUser?.user.roles[0].name === 'ADMINRO' ? (
          <ActionsCellContainer>
            <Tooltip title={t('admin_action.download_excel')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <DownloadOutlined
                  className="update"
                  onClick={() => {
                    handleDownloadExcel(record.id, record.carrier.businessName)
                  }}
                />
              </div>
            </Tooltip>
          </ActionsCellContainer>
        ) : (
          <ActionsCellContainer>
            <Tooltip title={t('admin_action.download_excel')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <DownloadOutlined
                  className="update"
                  onClick={() => {
                    handleDownloadExcel(record.id, record.carrier.businessName)
                  }}
                />
              </div>
            </Tooltip>
            <Tooltip title={t('admin_action.enter_hes')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <Button
                  className="update"
                  onClick={() => {
                    handleInputHes(record.id)
                  }}
                >
                  HES
                </Button>
              </div>
            </Tooltip>
          </ActionsCellContainer>
        ),
    },
  ]

  if (isLoading) {
    return <Spin />
  }
  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <DatePicker
              format="YYYY-MM-DD"
              onChange={handleDateFilterChange}
              value={dateFilter}
              allowClear
              placeholder={t('admin_action.filter_by_date')}
            />
            <StyledSelect
              maxTagCount="responsive"
              mode="multiple"
              allowClear
              placeholder={t('admin_action.search_by_TTE')}
              value={selectedCarriers}
              onChange={handleCarrierNameSearch}
            >
              {uniqueCarrierNames.map((carrierName: string) => (
                <StyledSelect.Option key={carrierName} value={carrierName}>
                  {carrierName}
                </StyledSelect.Option>
              ))}
            </StyledSelect>
            <StyledInput
              allowClear
              placeholder={t('admin_action.search_by_batch')}
              value={loteFilter}
              onChange={handleHesFilterChangeLote}
            />
          </div>
        </div>
      </StyledRow>
      <>
        <StyledRow className="ticket-handler">
          <div style={{ flex: 1 }} />
          {isLoading ? <Spin /> : <div style={{ flex: 1 }} />}
        </StyledRow>
        <Table
          className="stripped-table"
          columns={columnsBatch}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 10,
            onChange: (page, pageSize) => {
              setFilters({
                ...filters,
                limit: pageSize ?? 5,
                page,
              })
            },
          }}
        />
      </>
      <div>
        <Modal title="Ingresar Hes" open={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
          <Input
            maxLength={100}
            value={numero}
            onChange={e => handleNumeroChange(e)}
            style={{ width: '100%' }}
          />
        </Modal>
      </div>
    </ContentContainer>
  )
}

export { ClosedAdmin }
